// Support data and lookups for the /photon pages.

import {
  MODEL_FAMILIES,
  RELEASE_MODELS,
  SUPPORT_MATRIX,
} from "./generated/release-data";

export { MODEL_FAMILIES, RELEASE_MODELS, SUPPORT_MATRIX };
export type { ModelFamily, SupportRow } from "./release-types";

/** "Aug 3, 2026" - stable across locales because locale and zone are pinned. */
export const formatMatrixDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });

// ---- URL slugs and lookups for the generated detail pages ----
// (/photon/models/<family-slug>, /photon/hardware/<chip-slug>)

/** Lowercase URL slug: "Jetson AGX Orin" → "jetson-agx-orin". */
export const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/** Distinct chips with production support, sorted for the hardware index. */
export const HARDWARE_CHIPS = [
  ...new Set(
    SUPPORT_MATRIX.filter((r) => r.status === "production").map((r) => r.chip),
  ),
].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

/** Resolve a URL slug back to a chip name ("h100" → "H100"). */
export const chipForSlug = (slug: string) =>
  HARDWARE_CHIPS.find((chip) => slugify(chip) === slug);

/** Production rows for one chip. */
export const rowsForChip = (chip: string) =>
  SUPPORT_MATRIX.filter((r) => r.chip === chip && r.status === "production");

/** Production rows for one model family name ("Qwen 3"). */
export const rowsForFamily = (family: string) =>
  SUPPORT_MATRIX.filter(
    (r) => r.family === family && r.status === "production",
  );

/** Family record by its URL slug ("qwen-3"). */
export const familyForSlug = (slug: string) =>
  MODEL_FAMILIES.find((f) => f.id === slug);

/** Family URL slug from its display name ("Qwen 3" → "qwen-3"). */
export const familySlugForName = (family: string) =>
  MODEL_FAMILIES.find((f) => f.name === family)?.id;

/** Chips a family runs on in production, deduplicated. */
export const chipsForFamily = (family: string) => [
  ...new Set(rowsForFamily(family).map((r) => r.chip)),
];
